import type { PropsWithChildren } from "react";

import { X as CloseIcon, Menu02 } from "@untitledui/icons";
import { useState } from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Modal as AriaModal,
	ModalOverlay as AriaModalOverlay,
} from "react-aria-components";
import { Link, useLocation } from "react-router";

import { OrgControlLogo } from "@/assets/logos/org-control-logo";
import { ROUTES } from "@/constants/routes";
import { useOrgSelection } from "@/context/use-org-selection";
import { cx } from "@/utils/cx";

/**
 * Mobile top bar + slide-over drawer (dashboard `MobileNavigationHeader`).
 * Desktop sidebar is handled separately; this is `lg:hidden` only.
 *
 * Open state is keyed to pathname + org so NavLink navigation or an org
 * switch closes the drawer. Org hydration (`""` → real code) keeps it open.
 */
export const MobileNavigationHeader = ({ children }: PropsWithChildren) => {
	const { pathname } = useLocation();
	const { isLoading, selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? "";
	const [held, setHeld] = useState<null | { org: string; path: string }>(null);

	let nextHeld = held;
	if (held !== null) {
		if (held.path === pathname && held.org === "" && orgCode !== "") {
			// Promote "" → orgCode so loading orgs does not shut the drawer.
			nextHeld = { org: orgCode, path: pathname };
		} else if (held.path !== pathname || held.org !== orgCode) {
			// Computed close (org/path change) never fires onOpenChange — clear stale
			// held so a later return to the old org cannot reopen the drawer alone.
			nextHeld = null;
		}
	}
	if (nextHeld !== held) {
		setHeld(nextHeld);
	}

	const isOpen =
		nextHeld !== null && nextHeld.path === pathname && nextHeld.org === orgCode;

	return (
		<header
			className="flex h-16 items-center justify-between border-b border-secondary bg-primary py-3 pr-2 pl-4 lg:hidden"
			id="mobile-header"
		>
			<div className="flex w-full items-center justify-between gap-2">
				<div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2">
					<Link
						className="text-lg font-semibold tracking-tight text-primary"
						to={ROUTES.ORGANIZATION_LOCATIONS}
					>
						<OrgControlLogo isMobile />
					</Link>
					<span className="truncate text-xs text-secondary md:text-sm">
						{isLoading ? (
							<>Loading organizations…</>
						) : (
							selected && <>{selected.name}</>
						)}
					</span>
				</div>
			</div>
			<AriaDialogTrigger
				isOpen={isOpen}
				onOpenChange={(open) =>
					setHeld(open ? { org: orgCode, path: pathname } : null)
				}
			>
				<AriaButton
					aria-label="Expand navigation menu"
					className="group flex items-center justify-center rounded-lg bg-primary p-2 text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
				>
					<Menu02 className="size-6 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
					<CloseIcon className="absolute size-6 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
				</AriaButton>
				<AriaModalOverlay
					className={({ isEntering, isExiting }) =>
						cx(
							"fixed inset-0 z-50 cursor-pointer bg-neutral-900/70 pr-14 backdrop-blur-md lg:hidden",
							isEntering && "duration-300 ease-in-out animate-in fade-in",
							isExiting && "duration-200 ease-in-out animate-out fade-out",
						)
					}
					isDismissable
				>
					{({ state }) => (
						<>
							<AriaButton
								aria-label="Close navigation menu"
								className="fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-white outline-focus-ring hover:bg-white/10 hover:text-fg-white focus-visible:outline-2 focus-visible:outline-offset-2"
								onPress={() => state.close()}
							>
								<CloseIcon className="size-6" />
							</AriaButton>

							<AriaModal className="relative h-dvh w-full max-w-(--width-main-sidebar) min-w-65 cursor-auto overflow-hidden bg-linear-45 from-linear-secondary to-linear-primary will-change-transform">
								<AriaDialog className="relative h-full outline-hidden focus:outline-hidden">
									{children}
								</AriaDialog>
							</AriaModal>
						</>
					)}
				</AriaModalOverlay>
			</AriaDialogTrigger>
		</header>
	);
};
